import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { isScopeGuardRejection } from '../merchantErrors'
import { reportClientDiagnostic } from '../clientDiagnostics'
import { shopifyUiTracker, shopifyUiFailure, trackShopifyAction } from '../shopifyUiTracking'
import type { PageDeclaration, PageActionName, PageSourceName } from '../page-declaration'
import {
  actionButtonState,
  pageInteractionState,
  applyNewerData,
  initialPageStore,
  isAbortError,
  pageErrorFrom,
  reduceSourceUpdate,
  reduceStoreEvent,
  type LifecycleEvent,
  type PageError,
  type PageErrorName,
  type PageMoment,
  type PageStore,
  type SourceSignal,
} from '../page-state'
import type { SourceCheck } from '../page-error-diagnostics'
import type { shopifyApiFetch } from '../shopifyApiFetch'
import { beginRequestFeedback, settleRequestFeedback, requestFailures, type FeedbackToken } from '../page-feedback'
import type { ShopifyAppBridge } from '../shopifyApiFetch'

type ShopifyClient = Parameters<typeof shopifyApiFetch>[0] & Pick<ShopifyAppBridge, 'toast'>

type SourceData<D extends PageDeclaration, S extends PageSourceName<D>> = D['sources'][S]['check'] extends {
  safeParse(value: unknown): infer R
}
  ? Extract<R, { success: true }> extends { data: infer T }
    ? T
    : unknown
  : unknown

type ActionArgs<D extends PageDeclaration, A extends PageActionName<D>> = D['actions'][A] extends {
  check: (input: infer I) => unknown
}
  ? [input: I]
  : []

/** Why `run` did not start the action; a validation result, not an error report. */
export type ActionBlockedReason = 'busy' | 'not-ready' | PageErrorName

/**
 * What `run` resolves to, once the action has ended. It never rejects: the lifecycle bookkeeping (done,
 * failed, cancelled) is already finished, so callers only read this for merchant messages and navigation.
 */
export type ActionResult<T> =
  | { status: 'done'; value: T }
  | { status: 'failed'; error: PageError; cause: unknown }
  | { status: 'cancelled' }
  | { status: 'blocked'; reason: ActionBlockedReason }

function requiredSources<D extends PageDeclaration>(declaration: D): PageSourceName<D>[] {
  return Object.entries(declaration.sources)
    .filter(([, source]) => source.required)
    .map(([name]) => name as PageSourceName<D>)
}

/**
 * The only bridge between a page and its lifecycle. The helper reports data moments itself from the
 * page's query results (so cached data still arrives); pages call the returned functions only from
 * click handlers. `run` is the only way to start a declared action. Events and error reports carry fixed identifiers only (moment, source/action
 * name, error name, code), never payloads.
 */
export function usePageLifecycle<D extends PageDeclaration>(
  declaration: D,
  visitKey: string,
  shopify: ShopifyClient,
  queries: Partial<Record<PageSourceName<D>, SourceSignal>>,
  /** IDs of what the page is showing; every event and error report carries them. */
  ids: { setupId?: string | null; draftId?: string | null } = {},
) {
  type S = PageSourceName<D>
  type A = PageActionName<D>
  type Store = PageStore<S, A>
  const fresh = (): Store => initialPageStore<S, A>(visitKey, requiredSources(declaration))
  // The one copy of everything: lifecycle state, checked data, handled update times, revision.
  const store = useRef<{ current: Store; listeners: Set<() => void> }>({
    current: fresh(),
    listeners: new Set(),
  })
  const subscribe = useCallback((listener: () => void) => {
    store.current.listeners.add(listener)
    return () => {
      store.current.listeners.delete(listener)
    }
  }, [])
  // A new visit replaces the whole store; the same object is returned until it changes.
  const getSnapshot = () => {
    if (store.current.current.state.key !== visitKey) store.current.current = fresh()
    return store.current.current
  }
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const state = snapshot.state
  // An editor remount preserves the action; leaving the visit or losing required readiness cancels it.
  const aborter = useRef<AbortController | null>(null)

  const { setupId, draftId } = ids
  const sendError = useCallback(
    (moment: PageMoment, error: PageError, operation?: string) =>
      reportClientDiagnostic(shopify, {
        kind: 'error',
        schema: `page-lifecycle:${declaration.name}`,
        paths: operation ? [moment, operation, error.name] : [moment, error.name],
        operation: operation ?? 'page',
        code: error.code ?? error.name,
        ...(error.diagnostics?.status !== undefined ? { status: error.diagnostics.status } : {}),
        ...(error.diagnostics?.correlationId ? { correlationId: error.diagnostics.correlationId } : {}),
        ...(error.diagnostics?.validationPath ? { validationPath: error.diagnostics.validationPath } : {}),
        ...(setupId ? { setupId } : {}),
        ...(draftId ? { draftId } : {}),
      }),
    [shopify, declaration.name, setupId, draftId],
  )

  const commit = useCallback((next: Store) => {
    const before = store.current.current
    if (next === before) return
    store.current.current = next
    for (const listener of store.current.listeners) listener()
  }, [])
  // An event from an older visit has another key, so the transition returns the same state (R-a).
  const send = useCallback(
    (event: LifecycleEvent<S, A>) => commit(reduceStoreEvent(store.current.current, event)),
    [commit],
  )

  // Source effect: announce the visit once per key (StrictMode runs effects twice), then report each
  // source's new data or error once per update time. Cached data has an update time too, so it arrives.
  const stamp = Object.entries<SourceSignal | undefined>(queries)
    .map(([name, q]) => `${name}:${q?.identity}:${q?.dataUpdatedAt}:${q?.errorUpdatedAt}`)
    .join('|')
  useEffect(() => {
    const subscriptions: Array<() => void> = []
    const hasReadiness = Object.values(declaration.sources).some((source) => source.isReady)
    const isReady = hasReadiness
      ? (data: unknown, source: S) => {
          const predicate = declaration.sources[source].isReady as ((data: unknown) => boolean) | undefined
          return predicate ? predicate(data) : true
        }
      : undefined
    for (const [name, signal] of Object.entries<SourceSignal | undefined>(queries)) {
      if (!signal || !(name in declaration.sources)) continue
      const source = name as S
      const check = (value: unknown) => declaration.sources[source].check.safeParse(value) as ReturnType<SourceCheck>
      const accept = (current: SourceSignal) => {
        if (store.current.current.state.key !== visitKey) return
        const { store: next, reports } = reduceSourceUpdate(store.current.current, source, current, check, isReady)
        if (next.state.status !== 'ready') aborter.current?.abort()
        commit(next)
        for (const report of reports) {
          if (report.type === 'failed') sendError(report.moment, report.error, `source:${source}`)
        }
      }
      if (signal.subscribe) subscriptions.push(signal.subscribe(accept))
      else accept(signal)
    }
    return () => {
      for (const unsubscribe of subscriptions) unsubscribe()
    }
  }, [visitKey, stamp])

  const changed = useCallback(() => {
    send({ type: 'changed', key: visitKey })
  }, [visitKey, send])

  const attempt = useRef(0)
  const beginFeedback = useCallback(
    (operation: A): FeedbackToken => {
      const token = { visit: visitKey, operation, attempt: ++attempt.current }
      if (store.current.current.state.key === visitKey)
        commit({
          ...store.current.current,
          feedback: beginRequestFeedback(store.current.current.feedback ?? {}, token),
        })
      return token
    },
    [visitKey, commit],
  )
  const finishFeedback = useCallback(
    (token: FeedbackToken, outcome: 'accepted' | 'cancelled' | PageError) => {
      const before = store.current.current
      const feedback = settleRequestFeedback(before.feedback ?? {}, before.state.key, token, outcome)
      if (feedback === before.feedback || token.visit !== before.state.key) return false
      commit({ ...before, feedback })
      return true
    },
    [commit],
  )
  const failFeedback = useCallback(
    (token: FeedbackToken, cause: unknown) => {
      const error = pageErrorFrom(cause)
      if (finishFeedback(token, error) && !isScopeGuardRejection(cause))
        sendError('error', { ...error, code: error.code ?? `action:${token.operation}` }, `action:${token.operation}`)
    },
    [finishFeedback, sendError],
  )

  /**
   * Starts a declared action: checks it, runs `work(signal)`, and ends the action exactly once (done, failed
   * with a named error, or cancelled when `work` rejects with an abort). `input` is what the action's check reads.
   */
  const run = useCallback(
    async <N extends A, T>(
      action: N,
      work: (signal: AbortSignal) => Promise<T>,
      ...input: ActionArgs<D, N>
    ): Promise<ActionResult<T>> => {
      const before = store.current.current.state
      if (before.key !== visitKey || before.status !== 'ready') return { status: 'blocked', reason: 'not-ready' }
      if (before.running !== null) return { status: 'blocked', reason: 'busy' }
      const check = declaration.actions[action].check as ((input: unknown) => PageErrorName | null) | undefined
      const reason = check?.(input[0])
      const tracker = shopifyUiTracker(shopify)
      if (reason) {
        tracker.blocked(action, reason)
        return { status: 'blocked', reason }
      }
      const token = beginFeedback(action)
      send({ type: 'action-requested', key: visitKey, action })
      const uiAttempt = tracker.request(action)
      if (!aborter.current || aborter.current.signal.aborted) aborter.current = new AbortController()
      const actionController = aborter.current
      try {
        const value = await work(actionController.signal)
        if (actionController.signal.aborted || store.current.current.state.key !== visitKey)
          throw new DOMException('Action cancelled', 'AbortError')
        tracker.completed(uiAttempt)
        send({ type: 'action-done', key: visitKey, action, saves: declaration.actions[action].saves })
        const accepted = finishFeedback(token, 'accepted')
        const success = declaration.actions[action].feedback?.success
        if (accepted && success) {
          try {
            shopify.toast?.show(success)
          } catch (cause) {
            console.error('[page-lifecycle] Native toast unavailable', pageErrorFrom(cause).name)
            // Presentation failure cannot undo an acknowledged write or ask the merchant to repeat it.
            sendError('error', { name: 'unexpected', code: 'PAGE_TOAST_UNAVAILABLE' }, action)
          }
        }
        return { status: 'done', value }
      } catch (cause) {
        if (isAbortError(cause)) {
          tracker.cancelled(uiAttempt, store.current.current.state.key !== visitKey ? 'scope-change' : 'aborted')
          finishFeedback(token, 'cancelled')
          send({ type: 'action-cancelled', key: visitKey, action })
          return { status: 'cancelled' }
        }
        const error = pageErrorFrom(cause)
        tracker.failed(uiAttempt, shopifyUiFailure(cause))
        console.error(`[page-lifecycle:${declaration.name}] ${action} failed: ${error.name}`)
        send({ type: 'action-failed', key: visitKey, action, error })
        failFeedback(token, cause)
        return { status: 'failed', error, cause }
      }
    },
    [declaration, visitKey, send, sendError, beginFeedback, finishFeedback, failFeedback, shopify],
  )
  // Leaving the page or visit stops running work. Ordinary ready refreshes do not cancel an action;
  // only explicit required-source revocation in the source effect above does.
  useEffect(
    () => () => {
      aborter.current?.abort()
    },
    [visitKey],
  )
  /** Loading/disabled for an action's button, read from the page so it survives an editor remount. */
  const button = useCallback((action: A) => actionButtonState(state, action), [state])
  const fail = useCallback(
    (error: PageError) => {
      send({ type: 'failed', key: visitKey, error })
      sendError('error', error)
    },
    [visitKey, send, sendError],
  )
  const applyNewer = useCallback(() => {
    trackShopifyAction(shopify, 'load-saved', () => commit(applyNewerData(store.current.current)))
  }, [commit, shopify])
  // Conflict recovery: arm the page so the next refreshed source (from the refetch the caller triggers) is applied
  // straight away, replacing unsaved edits, instead of raising the newer-version chooser. Reuses `applyNewerData`.
  const requestLoadSaved = useCallback(() => {
    if (store.current.current.state.key === visitKey) commit({ ...store.current.current, loadingSaved: true })
  }, [visitKey, commit])
  const dismissNewer = useCallback(() => {
    trackShopifyAction(shopify, 'keep-editing', () => send({ type: 'newer-dismissed', key: visitKey }))
  }, [visitKey, send, shopify])
  const retryFailedLoad = useCallback(() => {
    if (store.current.current.state.key === visitKey && store.current.current.state.status === 'error')
      commit(initialPageStore<S, A>(visitKey, requiredSources(declaration)))
  }, [visitKey, declaration, commit])

  const applied = snapshot.applied as { [N in S]?: SourceData<D, N> }
  return {
    state,
    interactions: pageInteractionState(state, declaration.actions),
    feedback: requestFailures(declaration.actions, snapshot.feedback ?? {}),
    beginFeedback,
    finishFeedback,
    failFeedback,
    data: { applied, revision: snapshot.revision },
    changed,
    run,
    button,
    fail,
    applyNewer,
    requestLoadSaved,
    dismissNewer,
    retryFailedLoad,
  }
}
